import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  Badge,
  ClickAwayListener,
  Grow,
  IconButton,
  makeStyles,
  Popper
} from '@material-ui/core'
import NotificationsNoneOutlinedIcon from '@material-ui/icons/NotificationsNoneOutlined'
import MailIcon from '@material-ui/icons/Mail'
import DraftsIcon from '@material-ui/icons/Drafts'
import clsx from 'clsx'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import { useAdvisorNotifications } from '../../api/notifications'
import SydSwitch from '../commonDesign/SydSwitch'
import {
  loadAdvisorNotificationsUnreadOnlyFilter,
  storeAdvisorNotificationsUnreadOnlyFilter
} from '../../utils/advisorNotifications'
import Tooltip from '../atoms/Tooltip'
import ScrollContent from './ScrollContent'

dayjs.extend(relativeTime)
dayjs.extend(utc)

const NOTIFICATIONS_SCROLL_ITEM_THRESHOLD = 4
const NOTIFICATION_LIST_SCROLL_MAX_HEIGHT = '22rem'

const useStyles = makeStyles(() => ({
  anchor: {
    display: 'flex',
    alignItems: 'center'
  },
  countBadge: {
    backgroundColor: 'var(--color-danger-500)',
    color: 'var(--color-danger-050)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    minWidth: '1.125rem',
    height: '1.125rem'
  },
  menuPanel: {
    minWidth: 320,
    maxWidth: 400,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--tc-layout-background-color, var(--color-neutral-050))',
    border: '1px solid var(--border-color-neutral-normal)',
    borderRadius: 'var(--border-radius-md)',
    boxShadow: 'var(--shadow-elevation-2, 0 4px 16px rgba(0, 0, 0, 0.12))',
    overflow: 'hidden'
  },
  menuPanelScrollable: {
    maxHeight: `calc(${NOTIFICATION_LIST_SCROLL_MAX_HEIGHT} + 2.5rem)`
  },
  menuPanelContent: {
    display: 'flex',
    flexDirection: 'column'
  },
  menuPanelContentScrollable: {
    minHeight: 0,
    flex: 1
  },
  menuListScrollable: {
    minHeight: 0
  },
  menuHeader: {
    padding: '0.375rem 1rem',
    borderBottom: '1px solid var(--border-color-neutral-normal)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    lineHeight: 1.2,
    color: 'var(--color-neutral-700, #424242)',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem'
  },
  menuHeaderTitle: {
    flexShrink: 0
  },
  menuHeaderToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    flexShrink: 0
  },
  menuHeaderSwitchWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 19,
    height: 12
  },
  menuHeaderSwitch: {
    transform: 'scale(0.5)',
    transformOrigin: 'center center',
    margin: '-6px -9.5px'
  },
  toggleLabel: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 400,
    color: 'var(--color-neutral-500, #9e9e9e)',
    lineHeight: 1,
    userSelect: 'none'
  },
  toggleLabelActive: {
    fontWeight: 600,
    color: 'var(--color-neutral-700, #424242)'
  },
  menuItem: {
    display: 'block',
    width: '100%',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    padding: '0.75rem 1rem',
    backgroundColor: 'transparent',
    color: 'rgb(117, 117, 117)',
    fontFamily: 'var(--font-family-sans)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 400,
    borderBottom: '1px solid var(--border-color-neutral-subtle, var(--color-neutral-100))',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  menuItemUnread: {
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  menuItemBody: {
    marginBottom: '0.25rem'
  },
  menuItemBodyUnread: {
    color: 'var(--color-primary-500)',
    fontWeight: 600
  },
  menuItemFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem'
  },
  menuItemMeta: {
    flex: 1,
    minWidth: 0,
    fontSize: 'var(--font-size-xs)',
    color: 'rgb(117, 117, 117)',
    fontWeight: 400
  },
  menuItemIconRead: {
    fontSize: 'var(--font-size-xs)',
    color: 'rgb(117, 117, 117)',
    flexShrink: 0
  },
  markAsReadButton: {
    border: 'none',
    background: 'transparent',
    padding: '0.125rem',
    margin: '-0.125rem',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-primary-500)',
    flexShrink: 0,
    borderRadius: '50%',
    '&:focus-visible': {
      outline: '2px solid var(--color-primary-500)',
      outlineOffset: 1
    },
    '& $markAsReadIconHover': {
      display: 'none'
    },
    '&:hover $markAsReadIconDefault, &:focus-visible $markAsReadIconDefault': {
      display: 'none'
    },
    '&:hover $markAsReadIconHover, &:focus-visible $markAsReadIconHover': {
      display: 'inline-block'
    }
  },
  markAsReadIconDefault: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-primary-500)',
    display: 'inline-block'
  },
  markAsReadIconHover: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-primary-500)'
  },
  emptyState: {
    padding: '1.5rem 1rem',
    textAlign: 'center',
    color: 'var(--color-neutral-600)',
    fontSize: 'var(--font-size-sm)'
  },
  loadingState: {
    padding: '1rem',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-neutral-600)'
  }
}))

function formatNotificationTime (notification) {
  const raw = notification.sequenceDate || notification.createdDate
  if (!raw) {
    return ''
  }
  return dayjs.utc(raw).local().fromNow()
}

function AdvisorNotificationsMenu () {
  const classes = useStyles()
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [unreadOnly, setUnreadOnly] = useState(loadAdvisorNotificationsUnreadOnlyFilter)
  const {
    notifications,
    unreadCount,
    markAsRead,
    isLoading,
    isError
  } = useAdvisorNotifications()

  const displayCount = unreadCount > 99 ? '99+' : unreadCount

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleUnreadOnlyChange = useCallback((_event, checked) => {
    setUnreadOnly(checked)
    storeAdvisorNotificationsUnreadOnlyFilter(checked)
  }, [])

  const visibleNotifications = useMemo(() => {
    if (!unreadOnly) {
      return notifications
    }
    return notifications.filter((notification) => !notification.read)
  }, [notifications, unreadOnly])

  const shouldScrollList = useMemo(() => {
    if (isLoading || isError || !notifications.length) {
      return false
    }
    return visibleNotifications.length > NOTIFICATIONS_SCROLL_ITEM_THRESHOLD
  }, [isLoading, isError, notifications.length, visibleNotifications.length])

  const handleNotificationClick = useCallback((notification) => {
    if (!notification.read) {
      markAsRead(notification)
    }

    if (notification.actionUrl) {
      window.open(notification.actionUrl, '_blank', 'noopener,noreferrer')
    }

    handleClose()
  }, [markAsRead, handleClose])

  const handleMarkAsReadClick = useCallback((event, notification) => {
    event.stopPropagation()
    if (!notification.read) {
      markAsRead(notification)
    }
  }, [markAsRead])

  const handleNotificationKeyDown = useCallback((event, notification) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleNotificationClick(notification)
    }
  }, [handleNotificationClick])

  const menuContent = useMemo(() => {
    if (isLoading) {
      return <div className={classes.loadingState}>Loading notifications…</div>
    }

    if (isError) {
      return <div className={classes.emptyState}>Unable to load notifications.</div>
    }

    if (!notifications.length) {
      return <div className={classes.emptyState}>No notifications</div>
    }

    if (!visibleNotifications.length) {
      return <div className={classes.emptyState}>No unread notifications</div>
    }

    return visibleNotifications.map((notification) => (
      <div
        key={notification.notificationId ?? notification.notificationKey}
        role='button'
        tabIndex={0}
        className={`${classes.menuItem} ${!notification.read ? classes.menuItemUnread : ''}`}
        onClick={() => handleNotificationClick(notification)}
        onKeyDown={(event) => handleNotificationKeyDown(event, notification)}
      >
        <div className={`${classes.menuItemBody} ${!notification.read ? classes.menuItemBodyUnread : ''}`}>{notification.body}</div>
        <div className={classes.menuItemFooter}>
          <div className={classes.menuItemMeta}>
            {formatNotificationTime(notification)}
            {notification.category ? ` · ${notification.category}` : ''}
          </div>
          {notification.read
            ? <DraftsIcon className={classes.menuItemIconRead} aria-hidden='true' />
            : (
              <Tooltip title='Mark as read'>
                <button
                  type='button'
                  className={classes.markAsReadButton}
                  onClick={(event) => handleMarkAsReadClick(event, notification)}
                  aria-label='Mark as read'
                >
                  <MailIcon className={classes.markAsReadIconDefault} aria-hidden='true' />
                  <DraftsIcon className={classes.markAsReadIconHover} aria-hidden='true' />
                </button>
              </Tooltip>
            )}
        </div>
      </div>
    ))
  }, [
    classes,
    notifications,
    visibleNotifications,
    isLoading,
    isError,
    handleNotificationClick,
    handleNotificationKeyDown,
    handleMarkAsReadClick
  ])

  return (
    <div className={classes.anchor} ref={anchorRef}>
      <IconButton
        onClick={handleToggle}
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
        data-testid={unreadCount > 0 ? 'advisor-notifications-badge' : 'advisor-notifications-button'}
      >
        <Badge
          badgeContent={displayCount}
          classes={{ badge: classes.countBadge }}
          invisible={unreadCount === 0}
          max={99}
        >
          <NotificationsNoneOutlinedIcon
            fontSize='small'
            style={{ color: 'var(--tc-layout-text-color)' }}
          />
        </Badge>
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-end'
        transition
        className='z-index-dropdown'
        style={{ zIndex: 10000 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
            <div className={clsx(classes.menuPanel, shouldScrollList && classes.menuPanelScrollable)}>
              <ClickAwayListener onClickAway={handleClose}>
                <div className={clsx(classes.menuPanelContent, shouldScrollList && classes.menuPanelContentScrollable)}>
                  <div className={classes.menuHeader}>
                    <span className={classes.menuHeaderTitle}>Notifications</span>
                    <div
                      className={classes.menuHeaderToggle}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <span className={clsx(classes.toggleLabel, !unreadOnly && classes.toggleLabelActive)}>
                        All
                      </span>
                      <div className={classes.menuHeaderSwitchWrap}>
                        <SydSwitch
                          className={classes.menuHeaderSwitch}
                          size='sm'
                          value={unreadOnly}
                          onChange={handleUnreadOnlyChange}
                          label='Show unread notifications only'
                        />
                      </div>
                      <span className={clsx(classes.toggleLabel, unreadOnly && classes.toggleLabelActive)}>
                        Unread
                      </span>
                    </div>
                  </div>
                  {shouldScrollList ? (
                    <ScrollContent
                      className={classes.menuListScrollable}
                      maxHeight={NOTIFICATION_LIST_SCROLL_MAX_HEIGHT}
                    >
                      {menuContent}
                    </ScrollContent>
                  ) : (
                    <div>{menuContent}</div>
                  )}
                </div>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </div>
  )
}

AdvisorNotificationsMenu.propTypes = {}

export default AdvisorNotificationsMenu
